import { createContext, useCallback, useContext, useEffect, useId, useState, type ReactNode } from 'react';
import { useBlocker } from 'react-router-dom';
import { Dialog } from './ui/dialog';
import { Button } from './ui/button';
interface GuardContext {
  setDirty: (id: string, value: boolean) => void;
  confirm: (action: () => void) => void;
}
const Context = createContext<GuardContext | null>(null);
export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  const [dirtyIds, setDirtyIds] = useState<Set<string>>(new Set());
  const [pending, setPending] = useState<(() => void) | null>(null);
  const dirty = dirtyIds.size > 0;
  const blocker = useBlocker(dirty);
  const setDirty = useCallback((id: string, value: boolean) => setDirtyIds(previous => {
    if (previous.has(id) === value) return previous;
    const next = new Set(previous); if (value) next.add(id); else next.delete(id); return next;
  }), []);
  const confirm = useCallback((action: () => void) => setPending(() => action), []);
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  const keep = () => { setPending(null); if (blocker.state === 'blocked') blocker.reset(); };
  const discard = () => {
    const action = pending; setPending(null);
    if (blocker.state === 'blocked') { setDirtyIds(new Set()); blocker.proceed(); }
    else action?.();
  };
  return <Context.Provider value={{ setDirty, confirm }}>{children}
    <Dialog open={Boolean(pending) || blocker.state === 'blocked'} onOpenChange={open => { if (!open) keep(); }} title="Discard unsaved changes?" description="Your changes have not been saved. Keep editing to save them, or discard them to continue.">
      <div className="form-actions"><Button variant="outline" onClick={keep}>Keep editing</Button><Button variant="destructive" onClick={discard}>Discard changes</Button></div>
    </Dialog>
  </Context.Provider>;
}
export function useUnsavedChanges(dirty: boolean) {
  const context = useContext(Context); const id = useId();
  if (!context) throw new Error('UnsavedChangesProvider is required');
  const { setDirty, confirm } = context;
  useEffect(() => { setDirty(id, dirty); return () => setDirty(id, false); }, [id, dirty, setDirty]);
  return (action: () => void) => { if (dirty) confirm(action); else action(); };
}
